import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Settings,
  RotateCcw,
  GripVertical,
  ChevronUp,
  ChevronDown,
  X,
} from "lucide-react";
import Button from "@components/ButtonComponent";
import { getMixedFontClass } from "@/utils/font-utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { InfoIcon } from "lucide-react";

const TopicsHierarchyModal = ({
  isOpen,
  onOpenChange,
  selectedTopics,
  topicsData,
  tempTopicsHierarchy,
  topicsHierarchy,
  draggedItem,
  dragOverIndex,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  moveFilterUp,
  moveFilterDown,
  getFilterLabel,
  hasHierarchyChanged,
  resetTopicHierarchy,
  getAvailableFilterTagsForTopic,
  isDefaultFilter,
  removeFilterFromHierarchy,
  addFilterToHierarchy,
  filtersData,
  handleOpenModal,
  handleCloseModal,
  handleCancelModal,
}) => {
  const resolveAppliesToTopic = (filterTag) => {
    const appliesToTopics = topicsData.Topics.filter((topic) =>
      topic.AvailableFilterTags.includes(filterTag)
    );
    return (
      <div
        key={filterTag}
        className="text-xs text-primary opacity-100 font-medium"
      >
        <Tooltip className="opacity-100">
          <TooltipTrigger>
            <InfoIcon className="w-4 h-4" />
          </TooltipTrigger>
          <TooltipContent className="text-xs !bg-white !opacity-100 p-3 py-2 italic font-semibold ">
            This filter applies to
            <span className="font-medium px-1 grid grid-cols-2 gap-1 mt-1">
              {appliesToTopics
                ?.map((topic) => topic.Label)
                .map((label) => (
                  <span key={label}>{label}</span>
                ))}
            </span>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (open) {
          handleOpenModal();
        } else {
          handleCancelModal();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          color="blue"
          size="sm"
          width="max"
          icon={<Settings className="w-4 h-4 mr-2" />}
          noTextSwap={true}
        >
          Edit Topics Hierarchy
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            <span>Edit Topics Hierarchy</span>
            <span className="text-sm text-gray-500 font-normal">
              <br />
              <br />
              <span className="text-sm text-gray-500 font-normal">
                Drag and drop to reorder the hierarchy
              </span>
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {selectedTopics.map((topicTag) => {
            const topic = topicsData.Topics.find((t) => t.Tag === topicTag);
            const hierarchy =
              tempTopicsHierarchy[topicTag] || topicsHierarchy[topicTag] || [];
            const availableFilters = getAvailableFilterTagsForTopic(topicTag);

            return (
              <div key={topicTag} className="border rounded-lg p-4">
                <h3 className="text-md font-semibold mb-3 flex items-center justify-between">
                  <div
                    className={`
                          flex items-center gap-2 
                          ${
                            hasHierarchyChanged(topicTag, true)
                              ? "text-primary dark:text-white"
                              : "text-gray-900 dark:text-white"
                          }
                        `}
                  >
                    {topic?.Label || topicTag}
                    <span className="text-xs dark:text-white text-gray-500 font-normal">
                      ({hierarchy.length} levels)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasHierarchyChanged(topicTag, true) && (
                      <Button
                        size="xs"
                        onClick={() => resetTopicHierarchy(topicTag)}
                        className="whitespace-nowrap"
                        icon={<RotateCcw className="w-3 h-3 mr-1" />}
                        text="Reset"
                        width="max"
                      ></Button>
                    )}
                  </div>
                </h3>
                <div className="space-y-2">
                  {hierarchy.map((filterTag, index) => {
                    const isDragged =
                      draggedItem?.topicTag === topicTag &&
                      draggedItem?.index === index;
                    const isDragOver =
                      dragOverIndex?.topicTag === topicTag &&
                      dragOverIndex?.index === index;
                    const canDrop =
                      draggedItem?.topicTag === topicTag &&
                      draggedItem?.index !== index;

                    return (
                      <div key={`${topicTag}-${filterTag}-${index}`}>
                        <div
                          draggable
                          onDragStart={(e) => onDragStart(e, topicTag, index)}
                          onDragEnd={onDragEnd}
                          onDragOver={(e) => onDragOver(e, topicTag, index)}
                          onDragLeave={onDragLeave}
                          onDrop={(e) => onDrop(e, topicTag, index)}
                          style={{
                            paddingLeft: `${(index + 1) * 10}px`,
                          }}
                          className={`
                          flex items-center dark:bg-gray-700  gap-3 p-2 rounded-md cursor-move transition-colors duration-150
                          ${
                            isDragged
                              ? "opacity-60 bg-primary-50 border border-primary-300 dark:border-primary-500"
                              : isDragOver && canDrop
                              ? "bg-primary-50 border border-primary-200"
                              : "bg-gray-50 hover:bg-gray-100 border border-transparent"
                          }
                          ${
                            !canDrop &&
                            draggedItem &&
                            draggedItem.topicTag !== topicTag
                              ? "opacity-40"
                              : ""
                          }
                        `}
                        >
                          <GripVertical
                            className={`w-4 h-4 ${
                              isDragged ? "text-primary-500 dark:text-white"  : "text-gray-400 dark:text-white"
                            }`}
                          />
                          <div className="flex  gap-1">
                            <Button
                              variant="ghost"
                              size="xs"
                              onClick={() => moveFilterUp(topicTag, index)}
                              disabled={index === 0}
                              title="Move up"
                              width="max"
                              className="h-6 !p-0 text-primary-400 dark:text-white hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              icon={<ChevronUp />}
                            />
                            <Button
                              variant="ghost"
                              size="lg"
                              onClick={() => moveFilterDown(topicTag, index)}
                              disabled={index === hierarchy.length - 1}
                              className="h-6 !p-0 text-primary-400 dark:text-white hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Move down"
                              width="max"
                              icon={<ChevronDown />}
                            />
                          </div>
                          <span
                            className={`text-xs font-medium ${
                              isDragged ? "text-primary-600 dark:text-white" : "text-gray-600 dark:text-white"
                            }`}
                          >
                            Level{" "}
                            <span
                              className={`${
                                isDragged
                                  ? "text-primary-700 dark:text-white"
                                  : "text-slate-600 dark:text-white"
                              } font-normal`}
                            >
                              {index}
                            </span>
                          </span>
                          <span
                            className={`text-sm flex-1 
                                              ${getMixedFontClass()} 
                                              ${
                                                isDragged
                                                  ? "text-primary-700 dark:text-white"
                                                  : " dark:text-white"
                                              }
                                              ${
                                                isDefaultFilter(
                                                  topicTag,
                                                  filterTag
                                                )
                                                  ? "font-semibold text-slate-700 dark:text-white"
                                                  : "text-slate-500 dark:text-white"
                                              }`}
                          >
                            {getFilterLabel(filterTag)}
                          </span>

                          {(() => {
                            const currentHierarchy =
                              tempTopicsHierarchy[topicTag] || [];
                            const hasArea = currentHierarchy.includes("AREA");
                            const hasOutlet =
                              currentHierarchy.includes("OUTLET");
                            const isAreaOrOutlet =
                              filterTag === "AREA" || filterTag === "OUTLET";
                            const canRemove =
                              !isAreaOrOutlet || (hasArea && hasOutlet);

                            return (
                              <Button
                                type="cancel"
                                color="red"
                                variant="ghost"
                                size="xs"
                                width="max"
                                onClick={() =>
                                  removeFilterFromHierarchy(topicTag, filterTag)
                                }
                                disabled={!canRemove}
                                className={`text-xs px-2 ${
                                  canRemove
                                    ? "text-red-500 hover:text-red-700 cursor-pointer"
                                    : "text-gray-400 cursor-not-allowed"
                                }`}
                                title={
                                  canRemove
                                    ? `Remove ${getFilterLabel(filterTag)}`
                                    : isAreaOrOutlet
                                    ? "Cannot remove - Topic requires at least one of Area or Outlet"
                                    : `Remove ${getFilterLabel(filterTag)}`
                                }
                                text="Remove"
                              />
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })}

                  {draggedItem?.topicTag === topicTag && (
                    <div
                      onDragOver={(e) =>
                        onDragOver(e, topicTag, hierarchy.length)
                      }
                      onDragLeave={onDragLeave}
                      onDrop={(e) => onDrop(e, topicTag, hierarchy.length)}
                      className={`
                      h-1 rounded-md transition-colors duration-150 dark:bg-gray-700
                      ${
                        dragOverIndex?.topicTag === topicTag &&
                        dragOverIndex?.index === hierarchy.length
                          ? "bg-primary-500"
                          : "bg-transparent hover:bg-primary-100"
                      }
                    `}
                    />
                  )}
                  {availableFilters.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <select
                        className="mt-2 w-full text-sm border dark:bg-gray-700 dark:border-gray-600 dark:text-white border-gray-200 rounded-md px-3 py-2 bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                        onChange={(e) => {
                          if (e.target.value) {
                            addFilterToHierarchy(topicTag, e.target.value);
                            e.target.value = "";
                          }
                        }}
                        defaultValue=""
                      >
                        <option value="" disabled>
                          {"Select a filter to add..."}
                        </option>
                        {availableFilters.map((filterTag) => (
                          <option key={filterTag} value={filterTag}>
                            {getFilterLabel(filterTag)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleCancelModal}
            size="xs"
            color="red"
            type="cancel"
          >
            Cancel
          </Button>
          <Button onClick={handleCloseModal} className="text-xs">
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TopicsHierarchyModal;
